import React from 'react';
import clsx from 'clsx';
import { AriaToggleButtonProps, useToggleButton } from '@react-aria/button';
import { FocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import { mergeProps } from '@react-aria/utils';
import { useFocusableRef } from '@react-spectrum/utils';
import { FocusableRef, PressEvents } from '@react-types/shared';
import { useToggleState } from '@react-stately/toggle';
import styles from '@danske/sapphire-css/components/button/button.module.css';

import {
  SapphireStyleProps,
  usePreventTouchEnd,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { ButtonWithIconProps, ButtonWithoutIconProps, Icon } from '../..';
import { BaseComponentKeys, GlobalDomAttributes } from '../../utils/types';

export type ToggleButtonProps = Pick<
  AriaToggleButtonProps<'button'>,
  | BaseComponentKeys
  | 'children'
  | 'isDisabled'
  | 'autoFocus'
  | 'onFocus'
  | 'onBlur'
> &
  SapphireStyleProps &
  GlobalDomAttributes &
  PressEvents & {
    /**
     * @default 'md'
     */
    size?: 'lg' | 'md' | 'sm';
    onChange?: (selected: boolean) => void;
    isSelected?: boolean;
    defaultSelected?: boolean;
  } & (ButtonWithIconProps | ButtonWithoutIconProps);

/**
 * A button with a toggleable `isSelected` state. By default, the selection is toggled when the
 * button is pressed, allowing user to switch between two states or modes.
 */
export const ToggleButton = React.forwardRef(function Button(
  { iconAlign = 'left', icon, size = 'md', ...otherProps }: ToggleButtonProps,
  ref: FocusableRef<HTMLButtonElement>
) {
  useThemeCheck();

  const { autoFocus, isDisabled } = otherProps;
  const domRef = useFocusableRef<HTMLButtonElement>(ref);
  const { hoverProps, isHovered } = useHover({ isDisabled });
  const { styleProps } = useSapphireStyleProps(otherProps);

  const iconWrapper = icon && (
    <span className={clsx(styles['sapphire-button__icon'])}>
      <Icon size={size === 'sm' ? 'sm' : size === 'md' ? 'sm' : 'md'}>
        {icon}
      </Icon>
    </span>
  );

  const state = useToggleState(otherProps);
  const { buttonProps, isPressed } = useToggleButton(otherProps, state, domRef);
  usePreventTouchEnd(domRef);

  return (
    <FocusRing focusRingClass={clsx(styles['is-focus'])} autoFocus={autoFocus}>
      <button
        {...mergeProps(buttonProps, hoverProps)}
        ref={domRef}
        style={{ ...styleProps.style }}
        className={clsx(
          styles['sapphire-button'],
          styles['js-focus'],
          styles['js-hover'],
          {
            [styles['is-active']]: isPressed,
            [styles['is-hover']]: isHovered,
            [styles['is-disabled']]: isDisabled,
            [styles['sapphire-button--sm']]: size === 'sm',
            [styles['sapphire-button--lg']]: size === 'lg',
          },
          state.isSelected
            ? styles['sapphire-button--selected']
            : styles['sapphire-button--unselected'],
          styleProps.className
        )}
      >
        {iconAlign === 'left' && iconWrapper}
        <span className={clsx(styles['sapphire-button__content'])}>
          {otherProps.children}
        </span>
        {iconAlign === 'right' && iconWrapper}
      </button>
    </FocusRing>
  );
});
